import * as pixisounds from 'pixi-sound';
import { SettingsManager, SettingState } from './SettingsManager';
import { Loader } from '../../Loader';
import Sound = PIXI.sound.Sound;
import { addSignal } from '../../utils/EventUtils';
import { Events } from '../Events';

enum SoundType {
    SOUND = 'SOUND',
    MUSIC = 'MUSIC'
}

export class SoundManager {

    protected sounds: Array<{ [soundName: string]: pixisounds.default.Sound }> = [];
    protected music: Array<{ [soundName: string]: pixisounds.default.Sound }> = [];

    protected paused: boolean = false;

    protected volumes: Map<string, number> = new Map<string, number>();

    private soundsLoaded: boolean = false;
    private static _instance: SoundManager;

    private musicEnabled: boolean = false;
    private sfxEnabled: boolean = false;
    private static soundNames: string[] = ['interact', 'moveV1LR', 'moveV1RL', 'moveV2LR', 'moveV2RL', 'moveV3LR', 'moveV3RL'];
    private static musicNames: string[] = ['ambiental'];
    private ambiancePlaying: boolean = false;

    private constructor() {
        Loader.getInstance().loadSounds(SoundManager.soundNames, SoundManager.musicNames, this.onSoundsLoaded.bind(this));
        if (SettingsManager.getInstance().getSettingsMusic() === SettingState.On) {
            this.musicEnabled = true;
        }
        if (SettingsManager.getInstance().getSettingsSFX() === SettingState.On) {
            this.sfxEnabled = true;
        }
        addSignal(this, Events.gameResumed, this.onResumed.bind(this));
        addSignal(this, Events.gamePaused, this.onPaused.bind(this));
        addSignal(this, Events.settingMusicSet, this.onSettingMusicSet.bind(this));
        addSignal(this, Events.settingSFXSet, this.onSettingSFXSet.bind(this));
        addSignal(this, Events.CONTENT_PAUSE_REQUESTED, this.onPaused.bind(this));
        addSignal(this, Events.CONTENT_RESUME_REQUESTED, this.onResumed.bind(this));
        addSignal(this, Events.AD_SDK_CANCELED, this.onResumed.bind(this));
    }

    private onSoundsLoaded(loader: PIXI.Loader): void {
        const soundsPath: string = 'assets/sounds/';

        this.soundsLoaded = true;
        SoundManager.soundNames.forEach((soundName: string): void => {
            this.add(soundName, loader.resources[soundsPath + soundName + '.mp3'], SoundType.SOUND);
            PIXI.sound.add(soundName, soundsPath + soundName + '.mp3');
        });
        SoundManager.musicNames.forEach((musicName: string): void => {
            this.add(musicName, loader.resources[soundsPath + musicName + '.mp3'], SoundType.MUSIC);
            PIXI.sound.add(musicName, soundsPath + musicName + '.mp3');
        });
        if (this.musicEnabled) {
            this.playAmbiance();
        }
    }

    private playAmbiance(): void {
        if (!this.ambiancePlaying) {
            this.playSound('ambiental', 0.5, true);
            this.ambiancePlaying = true;
        }
    }

    public static getInstance(): SoundManager {
        return this._instance || (this._instance = new this());
    }

    private add(soundName: string, sound: any, category: string): void {
        if (category === SoundType.SOUND) {
            this.sounds[soundName] = sound;
        } else {
            this.music[soundName] = sound;
        }
    }

    private muteMusic(): void {
        // tslint:disable-next-line:forin
        for (const soundName in this.music) {
            PIXI.sound.pause(soundName);
        }
    }

    private unmuteMusic(): void {
        // tslint:disable-next-line:forin
        for (const soundName in this.music) {
            PIXI.sound.resume(soundName);
        }
    }

    private muteSound(): void {
        // tslint:disable-next-line:forin
        for (const soundName in this.sounds) {
            PIXI.sound.pause(soundName);
        }
    }

    private unmuteSound(): void {
        // tslint:disable-next-line:forin
        for (const soundName in this.sounds) {
            PIXI.sound.resume(soundName);
        }
    }

    public playSound(soundName: string, fade?: number, loop: boolean = false, soundVolume?: number, complete?: () => any): void {
        if (!this.soundsLoaded || this.paused || document.hidden) {
            return;
        }
        let categoryOn: boolean;
        const sound: Sound = this.sounds[soundName] ? this.sounds[soundName].sound : this.music[soundName].sound;
        if (this.sounds[soundName]) {
            categoryOn = this.sfxEnabled;
        } else if (this.music[soundName]) {
            categoryOn = this.musicEnabled;
        }
        if (!categoryOn) {
            return;
        }
        if (typeof this.volumes.get(soundName) === typeof undefined) {
            if (soundVolume) {
                this.volumes.set(soundName, soundVolume);
            } else {
                this.volumes.set(soundName, 1);
            }
        }
        let volume: number;
        if (!this.paused) {
            volume = this.volumes.get(soundName);
        } else {
            volume = 0;
        }
        console.log('play sound ' + soundName);
        PIXI.sound.play(soundName, {loop: loop, complete: complete, volume: volume});
        if (fade) {
            sound.volume = 0;
            TweenLite.to(sound, fade, {
                volume: volume
            });
        }
    }

    private onPaused(): void {
        this.paused = true;
        PIXI.sound.pauseAll();
    }

    private onResumed(): void {
        this.paused = false;
        PIXI.sound.resumeAll();
    }

    private onSettingMusicSet(): void {
        if (SettingsManager.getInstance().getSettingsMusic() === SettingState.On) {
            this.musicEnabled = true;
            this.unmuteMusic();
            this.playAmbiance();
        } else {
            this.musicEnabled = false;
            this.muteMusic();
        }
    }

    private onSettingSFXSet(): void {
        if (SettingsManager.getInstance().getSettingsSFX() === SettingState.On) {
            this.sfxEnabled = true;
            this.unmuteSound();
        } else {
            this.sfxEnabled = false;
            this.muteSound();
        }
    }
}
